import {$qsAll} from '../../../@shared/ts/dom';
import {type RequiredPolicyCheckbox} from '../models/CheckoutData';
import {getLocaleText} from '../util/translation';

const policyCheckboxSelector = '.pp-required-policy-checkbox';
export const requiredPolicyErrorMessage = getLocaleText('Please read and accept the required policies to continue.');

function isVisiblePolicyCheckbox($checkbox: HTMLInputElement): boolean {
	return $checkbox.getClientRects().length > 0;
}

function policyCheckboxes(): HTMLInputElement[] {
	return $qsAll<HTMLInputElement>(policyCheckboxSelector);
}

function visiblePolicyCheckboxes(): HTMLInputElement[] {
	return policyCheckboxes().filter(isVisiblePolicyCheckbox);
}

function syncPolicyCheckboxVisibilityState(): HTMLInputElement[] {
	const $checkboxes = policyCheckboxes();

	for (const $checkbox of $checkboxes) {
		$checkbox.disabled = !isVisiblePolicyCheckbox($checkbox);
	}

	return $checkboxes;
}

function acceptedPolicyCheckboxNames(): Set<string> {
	return new Set(
		visiblePolicyCheckboxes()
			.filter($input => $input.checked)
			.map($input => $input.name),
	);
}

function focusPolicyCheckbox($checkbox: HTMLInputElement): void {
	$checkbox.scrollIntoView({block: 'center', inline: 'nearest'});
	$checkbox.focus({preventScroll: true});
}

export function renderRequiredPolicyCheckboxes(fields: RequiredPolicyCheckbox[] = []): void {
	const acceptedNames = acceptedPolicyCheckboxNames();

	$qsAll('.pp-required-policy-checkboxes', $container => {
		$container.innerHTML = '';

		for (const [index, field] of fields.entries()) {
			const $label = document.createElement('label');
			$label.classList.add('pp-required-policy-checkbox-label');

			const $input = document.createElement('input');
			$input.type = 'checkbox';
			$input.name = field.name;
			$input.value = field.value || '1';
			$input.required = field.required;
			$input.classList.add('pp-required-policy-checkbox');
			$input.dataset['policyCheckboxIndex'] = index.toString();
			$input.checked = acceptedNames.has(field.name);

			const $text = document.createElement('span');
			$text.classList.add('pp-required-policy-checkbox-text');
			$text.innerHTML = field.label;

			$label.append($input, $text);
			$container.append($label);
		}
	});
}

export function validateRequiredPolicyCheckboxes(): boolean {
	const $checkboxes = syncPolicyCheckboxVisibilityState();
	const $visibleCheckboxes = $checkboxes.filter(isVisiblePolicyCheckbox);
	const acceptedNames = acceptedPolicyCheckboxNames();

	for (const $checkbox of $checkboxes) {
		$checkbox.setCustomValidity('');
	}

	for (const $checkbox of $visibleCheckboxes) {
		if (!$checkbox.required || acceptedNames.has($checkbox.name)) {
			continue;
		}

		$checkbox.setCustomValidity(requiredPolicyErrorMessage);
		focusPolicyCheckbox($checkbox);
		$checkbox.reportValidity();
		return false;
	}

	return true;
}

export function acceptedRequiredPolicyCheckboxData(): Record<string, string> {
	const acceptedFields: Record<string, string> = {};

	for (const $checkbox of syncPolicyCheckboxVisibilityState().filter($checkbox => isVisiblePolicyCheckbox($checkbox) && $checkbox.checked)) {
		acceptedFields[$checkbox.name] = $checkbox.value || '1';
	}

	return acceptedFields;
}
